import * as Yup from 'yup';
import { FC, useState, useMemo } from 'react';
import {
  Box,
  Icon,
  withStyles,
  TextField,
  NumbersOnly,
  Card,
  ElementContainer,
  capitalizeFirstLetter,
  capitalizeFirstLetterOfWords,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import { Title, Button, Checkbox } from 'UI';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { useDataFetchIndicator } from '@/hooks';
import usePostEditClientAccount from '@/hooks/useApi/usePostEditClientAccount';
import useCportal from '@/hooks/useApi/useCportal';
import styles from './CredentialsDialog.module.scss';

const cn = withStyles(styles);

type CredentialsDialogProps = {
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientID: string;
  onUpdate: () => void;
  onClose: () => void;
};

const CredentialsDialog: FC<CredentialsDialogProps> = ({
  clientName,
  clientEmail,
  clientPhone,
  clientID,
  onUpdate,
  onClose,
}) => {
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.CportalTab.CredentialsDialog' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const [emailAddress, setEmailAddress] = useState(clientEmail);
  const [phoneNumber, setPhoneNumber] = useState(clientPhone);
  const [shouldSendAccess, setShouldSendAccess] = useState(false);
  const { addCportalUser } = useCportal();
  const { editClientAccount } = usePostEditClientAccount();
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const isEmailValid = useMemo(() => {
    return Yup.string().email().required().isValidSync(emailAddress);
  }, [emailAddress]);

  const isPhoneValid = useMemo(() => {
    return Yup.string().required().isValidSync(phoneNumber);
  }, [phoneNumber]);

  const isFormValid = useMemo(() => {
    return isEmailValid && isPhoneValid;
  }, [isEmailValid, isPhoneValid]);

  const hasNewEmail = clientEmail !== emailAddress;
  const hasNewPhone = clientPhone !== NumbersOnly(phoneNumber);
  const hasNewInformation = hasNewEmail || hasNewPhone;
  const updatedInformation = useMemo(() => {
    if (hasNewEmail && hasNewPhone) return t('emailAndPhone', { defaultValue: 'Email address & phone number' });
    if (hasNewEmail) return t('emailAddress', { defaultValue: 'Email address' });
    return t('phoneNumber', { defaultValue: 'Phone number' });
  }, [hasNewEmail, hasNewPhone]);

  async function handleAlertDialog() {
    const result = await alertDialog.caution(
      <>
        {hasNewInformation
          ? t('enableAndUpdateTitle', { defaultValue: 'Enable cPortal & update information?' })
          : t('enableCportalTitle', { defaultValue: 'Enable cPortal?' })}
      </>,
      <Box flx="col" gapSize="24px">
        <div>
          {t('enableCportalDescription', {
            defaultValue:
              'This client will be given access to cPortal. This featur e cannot be turned off once enabled.',
          })}
        </div>
        {hasNewInformation && <div>{t('enableAndUpdateDescription', { information: updatedInformation })}</div>}
      </Box>,
      {
        abortLabel: tGlobal('Buttons.back', { defaultValue: 'Back' }),
        confirmLabel: tGlobal('Buttons.confirm', { defaultValue: 'Confirm' }),
      },
    );

    if (result === 'confirm') {
      try {
        setDataFetchingInfo(true);
        if (hasNewInformation) {
          await editClientAccount({
            ClientAccountID: clientID,
            PhoneNumber: NumbersOnly(phoneNumber),
            EmailAddress: emailAddress,
          });
        }

        await addCportalUser({
          PhoneNumber: NumbersOnly(phoneNumber),
          Email: emailAddress,
          ClientAccountID: clientID,
          SendWelcomeEmail: shouldSendAccess,
        });

        onUpdate();
        onClose();
      } catch (err) {
        if (err instanceof Error) {
          // the API returns either a duplicate email or duplicate phone number error, never both at once.
          const isDuplicateEmail = err.message.toLowerCase().includes('duplicate email');
          const isDuplicatePhone = err.message.toLowerCase().includes('duplicate phone number');
          if (isDuplicateEmail || isDuplicatePhone) {
            const field = isDuplicateEmail
              ? t('emailAddress', { defaultValue: 'Email address' })
              : t('phoneNumber', { defaultValue: 'Phone number' });
            const fieldCapitalized = capitalizeFirstLetterOfWords(field);
            const fieldLower = field.toLowerCase();
            const title = t('duplicateFieldTitle', { field: fieldCapitalized, defaultValue: 'Duplicate {{field}}' });
            const description = (
              <Box flx="col" gapSize="24px">
                <p>
                  {t('duplicateFieldDescriptionLine1', {
                    field: fieldLower,
                    defaultValue: 'A client account with the same {{field}} already exists.',
                  })}
                </p>
                <p>
                  {t('duplicateFieldDescriptionLine2', {
                    field: fieldLower,
                    defaultValue: 'Please enter a different {{field}} and try again.',
                  })}
                </p>
              </Box>
            );
            const confirmLabel = t('editFieldButton', { field: fieldCapitalized, defaultValue: 'Edit {{field}}' });
            setDataFetchingInfo(false);
            const result = await alertDialog.error(title, description, {
              abortLabel: tGlobal('Buttons.cancel', { defaultValue: 'Cancel' }),
              confirmLabel,
            });
            if (result === 'abort') {
              onClose();
            }
          } else {
            toast.neutral(err.message);
          }
        }
      } finally {
        setDataFetchingInfo(false);
      }
    }
  }

  return (
    <Card className={cn('card')}>
      <Box flx="col" gapSize="32px">
        <Box gapSize="16px" flx="col" ai="center">
          <div className={cn('iconContainer')}>
            <Icon icon="personGradient" width="40px" height="40px" color="none" />
          </div>
          <Box flx="col" gapSize="6px" ai="center">
            <Title level="3">{t('confirmCredentialTitle', { defaultValue: 'Confirm Credentials' })}</Title>
            <p className={cn('description')}>
              {t('confirmCredentialDescription', {
                defaultValue:
                  'This client will be given access to cPortal. This feature cannot be turned off once enabled.',
              })}
            </p>
          </Box>
        </Box>

        <Box flx="col" gapSize="24px">
          <ElementContainer
            label={t('clientAccountName', { defaultValue: 'Client Account Name' })}
            isBold={false}
            padding="0 0 8px 0"
            input={<TextField name="clientName" value={clientName} disabled variant="outlineShadow" />}
            containerClassName={cn('disabled')}
          />

          <ElementContainer
            label={capitalizeFirstLetter(t('emailAddress', { defaultValue: 'Email Address' }))}
            isBold={false}
            padding="0 0 8px 0"
            required
            input={
              <Box flx="col" gapSize="8px">
                <TextField
                  name="EmailAddress"
                  value={emailAddress}
                  variant="outlineShadow"
                  onChange={(e) => {
                    setEmailAddress(e.target.value);
                  }}
                />
                {hasNewEmail && (
                  <div className={cn('font13')}>
                    {t('updateEmailMessage', { defaultValue: 'This will update the client’s default email.' })}
                  </div>
                )}
              </Box>
            }
          />

          <ElementContainer
            label={capitalizeFirstLetter(t('phoneNumber', { defaultValue: 'Phone Number' }))}
            isBold={false}
            padding="0 0 8px 0"
            required
            input={
              <Box flx="col" gapSize="8px">
                <TextField
                  name="PhoneNumber"
                  value={phoneNumber}
                  variant="outlineShadow"
                  preset="tel"
                  onChange={(e) => {
                    setPhoneNumber(e.target.value);
                  }}
                />
                {hasNewPhone && (
                  <div className={cn('font13')}>
                    {t('updatePhoneMessage', { defaultValue: 'This will update the client’s default phone number.' })}
                  </div>
                )}
              </Box>
            }
          />
        </Box>

        <Box flx="col" gapSize="16px">
          <Box gapSize="6px">
            <Checkbox
              name="send-access-check"
              checked={shouldSendAccess}
              label={
                <div className={cn('accessDescription')}>
                  {t('sendAccess', {
                    defaultValue: 'Send cPortal access to the client account via email address.',
                  })}
                </div>
              }
              onChange={() => {
                setShouldSendAccess(!shouldSendAccess);
              }}
            />
          </Box>

          <Box gapSize="12px">
            <Button size="small" className={cn('button')} onClick={onClose}>
              {tGlobal('Buttons.back', { defaultValue: 'Back' })}
            </Button>
            <Button
              size="small"
              colorScheme="primary"
              className={cn('button')}
              onClick={handleAlertDialog}
              disabled={!isFormValid}
            >
              <Box ai="center">{t('enableCportalButton', { defaultValue: 'Enable cPortal' })}</Box>
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default CredentialsDialog;
